import * as mutations from "../graphql/mutations";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LoadingModal from "../components/modals/LoadingModal";
import NotificationSnackbar from "../components/NotificationSnackbar";
import SpecialText from "../components/SpecialText";
import EditModuleSettingsModal from "../components/modals/EditModuleSettingsModal";
import CircularProgressWithLabel from "../components/CircularProgressWithLabel";
import DisplayGeneratedText from "../components/DisplayGeneratedText";
import CreatePdfDocument from "../functions/CreatePdfDocument";
import DisplayModuleData from "../components/DisplayModuleData";
import DisplayTwoDimModuleDataCurrent from "../components/DisplayTwoDimModuleDataCurrent";
import { apiClient } from "../helpers/apiClient";
import { sortDataBySortingNumber } from "../helpers/helpers";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  CGI_BLACK,
  CGI_GRADIENT_A,
  CGI_RED,
  CGI_WARNING,
  packages,
  versions,
} from "../helpers/enums";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Box, Button, Container, Divider, Typography } from "@mui/material";
import {
  fetchAssessmentAssignmentById,
  fetchAssessmentById,
  fetchCustomerById,
  fetchEvaluationById,
  fetchEvaluationModuleDataByEvaluation,
  fetchModuleById,
  fetchQuestionById,
  fetchRecommendationsByRequirement,
  fetchRequirementAnswerByPerformAssessment,
  fetchRequirementsByQuestion,
} from "../helpers/apiRequests";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from "recharts";

export default function ShowEvaluation() {
  const { t, i18n } = useTranslation();
  const { evaluationId } = useParams();
  const navigate = useNavigate();

  const [pdfReady, setPdfReady] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [openLoading, setOpenLoading] = useState(true);
  const [selectedEvaluation, setSelectedEvaluation] = useState({});
  const [selectedEvaluationData, setSelectedEvaluationData] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState({});
  const [assessmentPackage, setSelectedAssessmentPackage] = useState(
    packages.BASIC,
  );
  const [assessmentVersion, setAssessmentVersion] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [loadError, setLoadError] = useState("");
  const [pdfSeed, setPdfSeed] = useState(1);

  const reloadPdf = () => {
    setPdfReady(false);
    setPdfSeed(Math.random());
  };

  function getEvaluationLanguage() {
    return (selectedEvaluation.language || i18n.resolvedLanguage || "en")
      .toLowerCase();
  }

  function tEvaluation(key, options = {}) {
    return t(key, { ...options, lng: getEvaluationLanguage() });
  }

  function hasModuleAnswer(moduleData) {
    return (
      moduleData.questionRating?.some((question) => question.hasAnswer) ||
      moduleData.twoDimPayload?.dataArray?.some((entry) => entry.hasAnswer)
    );
  }

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  function getModuleOrderFromGeneratedText(module, text) {
    if (!module.name || !text) {
      return null;
    }

    const moduleNamePattern = escapeRegExp(module.name);
    const linePattern = new RegExp(
      `(^|\\n)\\s*(\\d+)[.)]\\s*(\\*\\*)?${moduleNamePattern}(\\*\\*)?\\s*:`,
      "i",
    );
    const match = text.match(linePattern);

    if (!match) {
      return null;
    }

    return Number(match[2]);
  }

  function sortDataByGeneratedTextOrder(moduleData, text) {
    const modulesWithOrder = moduleData.map((module, index) => ({
      module,
      index,
      order: getModuleOrderFromGeneratedText(module, text),
    }));

    const orderedModules = new Array(moduleData.length);
    const unorderedModules = [];

    modulesWithOrder.forEach((item) => {
      const targetIndex = item.order - 1;

      if (
        Number.isInteger(item.order) &&
        targetIndex >= 0 &&
        targetIndex < moduleData.length &&
        !orderedModules[targetIndex]
      ) {
        orderedModules[targetIndex] = item.module;
      } else {
        unorderedModules.push(item.module);
      }
    });

    if (unorderedModules.length === moduleData.length) {
      return moduleData;
    }

    return orderedModules.map((module) => module || unorderedModules.shift());
  }

  function getAnswerTime(answer) {
    return new Date(answer?.updatedAt || answer?.createdAt || 0).getTime();
  }

  function groupLatestAnswersByRequirement(requirementAnswers) {
    const answersByRequirement = new Map();

    (requirementAnswers || []).forEach((answer) => {
      if (!answer?.requirementID) {
        return;
      }

      const current = answersByRequirement.get(answer.requirementID);
      if (!current || getAnswerTime(answer) >= getAnswerTime(current)) {
        answersByRequirement.set(answer.requirementID, answer);
      }
    });

    return answersByRequirement;
  }

  async function enrichEvalModuleData(evalModuleData, performedAssessmentID) {
    const requirementAnswersRes = performedAssessmentID
      ? await fetchRequirementAnswerByPerformAssessment(performedAssessmentID)
      : { items: [] };
    const answersByRequirement = groupLatestAnswersByRequirement(
      requirementAnswersRes.items || [],
    );

    const enriched = await Promise.all(
      evalModuleData.map(async (data) => {
        const module = await fetchModuleById(data.moduleID);

        const enrichedQuestionRatings = await Promise.all(
          (data.questionRating || []).map(async (qr) => {
            const [questionData, requirements] = await Promise.all([
              fetchQuestionById(qr.questionID),
              fetchRequirementsByQuestion(qr.questionID),
            ]);

            const enrichedRequirements = await Promise.all(
              requirements.map(async (req) => {
                const requirementAnswer = answersByRequirement.get(req.id);

                // Holt Recommendations für diese Requirement
                const recommendationsRes =
                  await fetchRecommendationsByRequirement(req.id);
                const recommendations = (recommendationsRes.items || [])
                  .slice() // Klonen, um Mutation zu vermeiden
                  .sort((a, b) => a.level - b.level);

                return {
                  ...req,
                  requirementAnswers: requirementAnswer
                    ? [requirementAnswer]
                    : [], // Array von RequirementAnswer
                  recommendations, // Array von Recommendation
                };
              }),
            );

            return {
              ...qr,
              questionData,
              requirements: enrichedRequirements,
            };
          }),
        );

        return {
          ...data,
          name: module?.title || data.moduleID,
          hideModule: false,
          sortingNumber: module?.sortingNumber ?? 0,
          questionRating: enrichedQuestionRatings,
        };
      }),
    );

    return enriched.sort(sortDataBySortingNumber);
  }

  async function saveAndRefetchText(newText) {
    try {
      await apiClient.graphql({
        query: mutations.updateEvaluation,
        variables: {
          input: {
            id: evaluationId,
            introText: newText,
          },
        },
      });
      await fetchEvaluation();
    } catch (error) {
      console.error(error);
    }
  }

  function toggleHideModuleById(targetId) {
    const updatedItems = selectedEvaluationData.map((item) => {
      if (item.id === targetId) {
        return {
          ...item,
          hideModule: !item.hideModule, // toggle the boolean
        };
      }
      return item;
    });

    setSelectedEvaluationData(updatedItems);
    reloadPdf();
  }

  async function fetchEvaluation() {
    setOpenLoading(true);
    setLoadError("");
    try {
      const evaluation = await fetchEvaluationById(evaluationId);
      if (!evaluation) {
        throw new Error(`Evaluation ${evaluationId} could not be loaded`);
      }

      setSelectedEvaluation(evaluation);
      const assessmentAssignment = await fetchAssessmentAssignmentById(
        evaluation.assessmentAssignmentID,
      );
      if (!assessmentAssignment) {
        throw new Error(
          `Assessment assignment ${evaluation.assessmentAssignmentID} could not be loaded`,
        );
      }

      setSelectedAssessmentPackage(assessmentAssignment.package);
      setAssessmentVersion(assessmentAssignment.version);
      const assessment = await fetchAssessmentById(
        assessmentAssignment.assessmentID,
      );
      if (!assessment) {
        throw new Error(
          `Assessment ${assessmentAssignment.assessmentID} could not be loaded`,
        );
      }

      setSelectedAssessment(assessment);
      const customer = await fetchCustomerById(assessmentAssignment.customerID);
      setSelectedCustomer(customer || {});
      const evaluationModuleData =
        (await fetchEvaluationModuleDataByEvaluation(evaluationId)) || [];
      if (
        evaluation.state === "FINISHED" &&
        evaluationModuleData.length === 0
      ) {
        throw new Error(
          `Evaluation ${evaluationId} has no readable module data`,
        );
      }

      if (versions[assessment.type] === versions.TWO_DIMENSIONAL) {
        const twoDimData = await Promise.all(
          evaluationModuleData.map(async (data) => {
            const module = await fetchModuleById(data.moduleID);
            let twoDimPayload = {};
            try {
              const parsed = JSON.parse(data.resultText || "{}");
              twoDimPayload =
                parsed?.type === "TWO_DIMENSIONAL" ? parsed : {};
            } catch {
              twoDimPayload = {};
            }

            return {
              ...data,
              name: module?.title || data.moduleID,
              hideModule: false,
              sortingNumber: module?.sortingNumber ?? 0,
              twoDimPayload,
            };
          }),
        );
        setSelectedEvaluationData(twoDimData.sort(sortDataBySortingNumber));
      } else {
        setSelectedEvaluationData(
          await enrichEvalModuleData(
            evaluationModuleData,
            evaluation.performedAssessmentID,
          ),
        );
      }
    } catch (error) {
      console.error(error);
      const message = t("errorMessage");
      setLoadError(message);
      setNotificationMessage(message);
      setOpenNotification(true);
    } finally {
      setOpenLoading(false);
    }
  }

  function getDateLocale() {
    const translatedLocale = t("timeSettings");
    if (translatedLocale && translatedLocale !== "timeSettings") {
      return translatedLocale;
    }

    if (i18n.resolvedLanguage?.toLowerCase().startsWith("de")) {
      return "de-DE";
    }

    if (i18n.resolvedLanguage?.toLowerCase().startsWith("fr")) {
      return "fr-FR";
    }

    return "en-US";
  }

  useEffect(() => {
    fetchEvaluation();
  }, []);

  const displayEvaluationData = sortDataByGeneratedTextOrder(
    selectedEvaluationData,
    selectedEvaluation?.introText || "",
  );

  const radarChartData = displayEvaluationData
    .filter((x) => !x.hideModule)
    .map((x, i) => ({
      ...x,
      displayIndex: i + 1,
      totalRatingInPercent: hasModuleAnswer(x)
        ? x.maxRating > 0
          ? x.rating / x.maxRating
          : 0
        : null,
    }));

  const answeredRadarPointCount = radarChartData.filter(
    (x) =>
      x.totalRatingInPercent !== null && x.totalRatingInPercent !== undefined,
  ).length;
  const shouldDrawRadarArea = answeredRadarPointCount >= 3;

  function renderRadarDot(props) {
    const value =
      props?.payload?.value ?? props?.payload?.payload?.totalRatingInPercent;
    const cx = props?.cx;
    const cy = props?.cy;

    if (
      value === null ||
      value === undefined ||
      !Number.isFinite(cx) ||
      !Number.isFinite(cy)
    ) {
      return null;
    }

    return <circle cx={cx} cy={cy} r={4} fill={CGI_RED} stroke={CGI_RED} />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mx: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: CGI_BLACK,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            navigate("/assessmentsEvaluations");
          }}
        >
          {t("backToCustomerSelection")}
        </Button>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Typography sx={{ ml: 3, fontSize: 16, fontWeight: 500 }}>
            {`${t("customer")}: ${selectedCustomer?.name || ""}`}
          </Typography>
          <Typography sx={{ fontSize: 16, ml: 2, pl: 2, borderLeft: 1 }}>
            {`${t("assessment")}: "${
              selectedAssessment?.name || ""
            } (${assessmentPackage})"`}
          </Typography>
          <Typography sx={{ fontSize: 16, ml: 2, pl: 2, borderLeft: 1 }}>
            {`${t("version")}: ${assessmentVersion}`}
          </Typography>
          <Typography sx={{ fontSize: 16, ml: 2, pl: 2, borderLeft: 1 }}>
            {selectedEvaluation?.createdAt
              ? `${t("evaluationFrom-")} ${new Date(
                  selectedEvaluation.createdAt,
                ).toLocaleString(getDateLocale(), {
                  timeZone: "Europe/Berlin",
                })}`
              : ""}
          </Typography>
        </Box>
      </Box>
      <Container maxWidth="xl" sx={{ px: { xs: 0, lg: 2 } }}>
        {loadError && (
          <Typography sx={{ mt: 5, color: CGI_RED, fontWeight: 500 }}>
            {loadError}
          </Typography>
        )}
        {selectedEvaluationData.length !== 0 && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: 1,
              }}
            >
              <Typography
                sx={{
                  mt: 5,
                  fontSize: 25,
                  color: CGI_RED,
                  fontWeight: 500,
                }}
              >
                {`${t("applicationName")}: ${t("assessmentEvaluation")} `}
                <SpecialText
                  style={{
                    pr: 1,
                    fontStyle: "italic",
                    background: CGI_GRADIENT_A,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {`"${selectedAssessment?.name || ""}"`}
                </SpecialText>
              </Typography>
              <Divider sx={{ mt: 2, mr: 5, borderColor: CGI_RED }} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                  ml: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 20,
                    userSelect: "none",
                  }}
                >
                  {`${tEvaluation("dataByModules")}:`}
                </Typography>
                <EditModuleSettingsModal
                  updateShowHideModule={toggleHideModuleById}
                  moduleData={displayEvaluationData}
                />
              </Box>
              <Divider sx={{ width: 500, mt: 0.5, borderColor: CGI_BLACK }} />
              <Typography
                sx={{
                  fontSize: 15,
                  mt: { xs: 2, lg: 3 },
                  mb: { xs: 5, lg: 3 },
                  mx: { xs: 1, lg: 2 },
                  fontFamily: "Inter",
                }}
              >
                {tEvaluation("moduleHeaderText")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: { xs: 1 / 2, lg: 1 / 3 },
                    justifyContent: "center",
                  }}
                >
                  <Box
                    id="dataByCategoryProgressId"
                    sx={{
                      ml: { xs: 1, lg: 3 },
                      display: "flex",
                      flexDirection: "column",
                      mr: 2,
                    }}
                  >
                    {displayEvaluationData
                      .filter((x) => !x.hideModule)
                      .map((categoryData, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                            pb: 1,
                            borderBottom: 1,
                            borderTop: index === 0 ? 1 : 0,
                            pt: index === 0 ? 1 : 0,
                            borderColor: "#aaa",
                          }}
                        >
                          {categoryData?.completelyAnswered ? (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  minWidth: 8 / 10,
                                  maxWidth: 8 / 10,
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{ fontSize: 16, fontWeight: 500, pr: 2 }}
                                >
                                  {index + 1}.
                                </Typography>
                                {(categoryData.rating /
                                  categoryData.maxRating) *
                                  100 <
                                  50 && (
                                  <WarningAmberIcon
                                    sx={{ color: CGI_RED, mr: 1, mb: 0.3 }}
                                  />
                                )}
                                <Typography
                                  sx={{ mb: 0.2, fontSize: { xs: 13, lg: 15 } }}
                                >
                                  {categoryData.name}: {categoryData.rating} /{" "}
                                  {categoryData.maxRating} {tEvaluation("points")}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                }}
                              >
                                <Divider
                                  flexItem
                                  orientation="vertical"
                                  sx={{ ml: 2, mr: 1 }}
                                />
                                <CircularProgressWithLabel
                                  progressValue={
                                    (categoryData.rating /
                                      categoryData.maxRating) *
                                    100
                                  }
                                />
                              </Box>
                            </>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                sx={{ fontSize: 16, fontWeight: 500, pr: 2 }}
                              >
                                {index + 1}.
                              </Typography>
                              <Typography
                                sx={{
                                  mb: 0.2,
                                  fontSize: { xs: 13, lg: 15 },
                                  color: CGI_WARNING,
                                }}
                              >
                                {categoryData.name}:{" "}
                                {tEvaluation("notCompletelyAnswered")}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: { xs: 1 / 2, lg: 2 / 3 },
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 450,
                  }}
                >
                  {
                    <ResponsiveContainer>
                      <RadarChart
                        id="radarChartId"
                        cx="50%"
                        cy="50%"
                        outerRadius="90%"
                        data={radarChartData}
                      >
                        <PolarGrid />
                        <PolarAngleAxis dataKey="displayIndex" />
                        <PolarRadiusAxis domain={[0, 1]} />
                        <Radar
                          name="Data"
                          dataKey="totalRatingInPercent"
                          stroke={CGI_RED}
                          fill={CGI_RED}
                          fillOpacity={shouldDrawRadarArea ? 0.6 : 0}
                          strokeWidth={shouldDrawRadarArea ? 2 : 0}
                          connectNulls={false}
                          isAnimationActive={false}
                          dot={renderRadarDot}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  }
                </Box>
              </Box>
              <DisplayGeneratedText
                text={selectedEvaluation?.introText || ""}
                saveAndRefetchText={saveAndRefetchText}
              />
              <Divider sx={{ my: 7 }} />
              <Typography
                sx={{
                  whiteSpace: "pre-line",
                  mx: 5,
                  fontSize: 16,
                  fontFamily: "Inter",
                }}
              >
                {tEvaluation("questionsHeaderText")}
              </Typography>
              <Divider sx={{ my: 4, width: 350, alignSelf: "center" }} />
            </Box>
            {displayEvaluationData
              .filter((x) => !x.hideModule)
              .map((data, index) =>
                versions[selectedAssessment.type] ===
                versions.TWO_DIMENSIONAL ? (
                  <DisplayTwoDimModuleDataCurrent
                    evalModuleData={data}
                    fetchEvaluation={fetchEvaluation}
                    setOpenLoading={setOpenLoading}
                    setNotificationMessage={setNotificationMessage}
                    setOpenNotification={setOpenNotification}
                    key={index}
                    reloadPdf={reloadPdf}
                    index={index}
                  />
                ) : (
                  <DisplayModuleData
                    evalModuleData={data}
                    fetchEvaluation={fetchEvaluation}
                    setOpenLoading={setOpenLoading}
                    setNotificationMessage={setNotificationMessage}
                    setOpenNotification={setOpenNotification}
                    key={index}
                    reloadPdf={reloadPdf}
                    index={index}
                  />
                ),
              )}
            {versions[selectedAssessment.type] !== versions.TWO_DIMENSIONAL && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  width: 1,
                  mt: 5,
                }}
                key={pdfSeed}
              >
                <Button variant="contained" disabled={!pdfReady}>
                  {
                    <PDFDownloadLink
                      document={
                        <CreatePdfDocument
                          moduleData={displayEvaluationData}
                          selectedCustomer={selectedCustomer}
                          pdfReady={pdfReady}
                          setPdfReady={setPdfReady}
                          selectedAssessment={selectedAssessment?.name || ""}
                          selectedEvaluation={selectedEvaluation}
                          introText={selectedEvaluation?.introText || ""}
                          moduleHeaderText={t("moduleHeaderText")}
                          questionsHeaderText={t("questionsHeaderText")}
                        />
                      }
                      fileName="assessment_result.pdf"
                      style={{ textDecoration: "none", color: "#fff" }}
                    >
                      {({ loading }) =>
                        loading || !pdfReady
                          ? t("generatingPDF")
                          : t("downloadPDF")
                      }
                    </PDFDownloadLink>
                  }
                </Button>
              </Box>
            )}
          </>
        )}
      </Container>
      <LoadingModal open={openLoading} />
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
    </Box>
  );
}
